import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';

function formatTanggal(dateStr) {
  if (!dateStr) return '-';
  const d = new Date(dateStr + (dateStr.includes('T') ? '' : 'T00:00:00'));
  return d.toLocaleDateString('id-ID', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }).replace(',', '');
}

function formatRupiah(val) {
  if (!val && val !== 0) return '-';
  return new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(val);
}

export default function SuratJalanDocument({
  order,
  shipment,
  companySettings = {},
  picName,
  picJabatan,
  signatureUrl,
  documentRef
}) {
  const kompanyName = companySettings?.nama_perusahaan || 'PT. SATALA DERMATECH ESSENTIAL';
  const kompanyAddr = companySettings?.alamat_perusahaan || 'Jl. Aster Kavling Paspampres, Kota Batu, Kec. Ciomas, Kabupaten Bogor, Jawa Barat 16610';
  const kota = companySettings?.kota || 'Bogor';
  const logoUrl = companySettings?.logo_url || '/logo.png';
  const stempelUrl = companySettings?.stempel_url || '/stempel-invoice.png';

  const today = new Date().toLocaleDateString('id-ID', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }).replace(',', '');
  const orderDate = order?.shipped_at ? formatTanggal(order.shipped_at) : (shipment?.shipped_at ? formatTanggal(shipment.shipped_at) : today);

  const noSurat = useMemo(() => {
    if (shipment?.no_surat_jalan) return shipment.no_surat_jalan;
    if (order?.surat_jalan_number) return order.surat_jalan_number;
    const baseNum = order?.order_number || order?.id?.split('-')[0] || '001';
    const sIdx = shipment?._index !== undefined ? shipment._index + 1 : 1;
    return `SJ-${baseNum}-${String(sIdx).padStart(2, '0')}`;
  }, [order, shipment]);

  const via = useMemo(() => {
    return shipment?.shipping_via || order?.shipping_via || order?.courier || 'Ekspedisi Satala';
  }, [shipment, order]);

  const isGabungan = !shipment && order?.shipments?.length > 1;

  // Gather products/items for delivery note
  const items = useMemo(() => {
    const list = [];
    if (shipment && shipment.items?.length > 0) {
      shipment.items.forEach((det) => {
        if (det.is_custom) {
          list.push({
            nama: det.variant_name || 'Item Tambahan / Custom',
            keterangan: det.catatan || det.keterangan || 'Pengembalian / Barang Tambahan',
            qty: Number(det.qty_akhir) || Number(det.qty_dikirim) || 0,
            satuan: det.satuan || 'pcs',
            is_custom: true,
          });
          return;
        }

        const prodInfo =
          (order?.products || []).find((p) => {
            const rawName = p.product_name || p.variant_name || 'Produk Utama';
            const strippedName = rawName.replace(/\s*\(\s*\d+\s*ml\s*\)/i, '').trim();
            const lSize = p.botol_ukuran_aktual || p.botol_ukuran_label || p.botol_ukuran_client || p.ukuran_botol_ml || 0;
            const vName = lSize ? `${strippedName} (${lSize}ml)` : rawName;
            return vName === det.variant_name || rawName === det.variant_name || strippedName === det.variant_name;
          }) || {};

        const labelSize =
          prodInfo.botol_ukuran_aktual || prodInfo.botol_ukuran_label || prodInfo.botol_ukuran_client || prodInfo.ukuran_botol_ml || 0;
        const sizeStr = labelSize ? `${labelSize} ml` : order?.ukuran_botol_ml ? `${order.ukuran_botol_ml} ml` : '';
        const notesStr = det.catatan || det.keterangan || '';
        const ketStr = notesStr || sizeStr || '-';

        list.push({
          nama: det.variant_name || order?.brand_name || 'Produk',
          keterangan: ketStr,
          qty: det.qty_akhir || det.qty_dikirim || 0,
          satuan: det.satuan || 'pcs',
          is_custom: false,
        });
      });
    } else if (!shipment && order?.shipments?.length >= 1) {
      const hasMultiple = order.shipments.length > 1;
      order.shipments.forEach((ship) => {
        const dObj = ship.date
          ? new Date(ship.date)
          : order.shipped_at
          ? new Date(order.shipped_at + (order.shipped_at.includes('T') ? '' : 'T00:00:00'))
          : new Date();
        const shipDateStr = dObj.toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric' });

        if (ship.items && ship.items.length > 0) {
          ship.items.forEach((det) => {
            if (det.is_custom) {
              list.push({
                nama: hasMultiple ? `${det.variant_name || 'Item Tambahan'} (${shipDateStr})` : (det.variant_name || 'Item Tambahan'),
                keterangan: det.catatan || det.keterangan || 'Pengembalian / Barang Tambahan',
                qty: Number(det.qty_akhir) || Number(det.qty_dikirim) || 0,
                satuan: det.satuan || 'pcs',
                is_custom: true,
              });
              return;
            }

            const prodInfo =
              (order?.products || []).find((p) => {
                const rawName = p.product_name || p.variant_name || 'Produk Utama';
                const strippedName = rawName.replace(/\s*\(\s*\d+\s*ml\s*\)/i, '').trim();
                const lSize = p.botol_ukuran_aktual || p.botol_ukuran_label || p.botol_ukuran_client || p.ukuran_botol_ml || 0;
                const vName = lSize ? `${strippedName} (${lSize}ml)` : rawName;
                return vName === det.variant_name || rawName === det.variant_name || strippedName === det.variant_name;
              }) || {};

            const labelSize =
              prodInfo.botol_ukuran_aktual || prodInfo.botol_ukuran_label || prodInfo.botol_ukuran_client || prodInfo.ukuran_botol_ml || 0;
            const sizeStr = labelSize ? `${labelSize} ml` : order?.ukuran_botol_ml ? `${order.ukuran_botol_ml} ml` : '';
            const notesStr = det.catatan || det.keterangan || '';

            // Hitung keterangan kemasan dus per item
            let dusInfoStr = '';
            const itQty = Number(det.qty_akhir) || Number(det.qty_dikirim) || 0;
            const itCap = Number(det.pcs_per_dus) || Number(ship?.pcs_per_dus) || Number(shipment?.pcs_per_dus) || 0;
            if (itCap > 0 && itQty > 0) {
              const full = Math.floor(itQty / itCap);
              const rem = itQty % itCap;
              if (rem === 0) {
                dusInfoStr = `${full} Dus (@${itCap} pcs)`;
              } else if (det.sisa_action === 'gabung') {
                dusInfoStr = `${full} Dus (@${itCap} pcs) · Sisa ${rem} pcs digabung Dus Campuran`;
              } else {
                dusInfoStr = `${full} Dus (@${itCap} pcs) + 1 Dus Sisa (${rem} pcs)`;
              }
            }

            const mainKet = notesStr || sizeStr || '';
            const ketStr = [mainKet, dusInfoStr].filter(Boolean).join(' | ') || '-';

            list.push({
              nama: hasMultiple ? `${det.variant_name || order.brand_name} (${shipDateStr})` : det.variant_name || order.brand_name,
              keterangan: ketStr,
              qty: Number(det.qty_akhir) || Number(det.qty_dikirim) || 0,
              satuan: det.satuan || 'pcs',
              is_custom: false,
            });
          });
        }
      });
    } else if (order?.shipping_details?.length > 0) {
      order.shipping_details.forEach((det) => {
        if (det.is_custom) {
          list.push({
            nama: det.variant_name || 'Item Tambahan / Custom',
            keterangan: det.catatan ? `Catatan: ${det.catatan}` : 'Pengembalian / Barang Tambahan',
            qty: Number(det.qty_akhir) || Number(det.qty_dikirim) || 0,
            satuan: det.satuan || 'pcs',
            is_custom: true,
          });
          return;
        }

        const prodInfo =
          (order?.products || []).find((p) => {
            const rawName = p.product_name || p.variant_name || 'Produk Utama';
            const strippedName = rawName.replace(/\s*\(\s*\d+\s*ml\s*\)/i, '').trim();
            const lSize = p.botol_ukuran_aktual || p.botol_ukuran_label || p.botol_ukuran_client || p.ukuran_botol_ml || 0;
            const vName = lSize ? `${strippedName} (${lSize}ml)` : rawName;
            return vName === det.variant_name || rawName === det.variant_name || strippedName === det.variant_name;
          }) || {};

        const labelSize =
          prodInfo.botol_ukuran_aktual || prodInfo.botol_ukuran_label || prodInfo.botol_ukuran_client || prodInfo.ukuran_botol_ml || 0;
        const sizeStr = labelSize ? `${labelSize} ml` : order?.ukuran_botol_ml ? `${order.ukuran_botol_ml} ml` : '';
        const notesStr = det.catatan || det.keterangan || '';
        const ketStr = notesStr || sizeStr || '-';

        list.push({
          nama: det.variant_name || order.brand_name,
          keterangan: ketStr,
          qty: det.qty_akhir || det.qty_dikirim || 0,
          satuan: det.satuan || 'pcs',
          is_custom: false,
        });
      });
    } else if (order?.products?.length > 0) {
      order.products.forEach((p) => {
        const rawName = p.product_name || p.variant_name || 'Produk Utama';
        const strippedName = rawName.replace(/\s*\(\s*\d+\s*ml\s*\)/i, '').trim();
        const lSize = p.botol_ukuran_aktual || p.botol_ukuran_label || p.botol_ukuran_client || p.ukuran_botol_ml || 0;
        const vName = lSize ? `${strippedName} (${lSize}ml)` : rawName;
        const sizeStr = lSize ? `${lSize} ml` : '';

        list.push({
          nama: vName,
          keterangan: sizeStr || '-',
          qty: Number(p.qty) || Number(p.target_qty) || 0,
          satuan: 'pcs',
          is_custom: false,
        });
      });
    } else {
      list.push({
        nama: order?.product_name || order?.brand_name || 'Produk Utama',
        keterangan: order?.ukuran_botol_ml ? `${order.ukuran_botol_ml} ml` : '-',
        qty: Number(order?.target_qty) || Number(order?.final_qty) || 0,
        satuan: 'pcs',
        is_custom: false,
      });
    }
    return list;
  }, [shipment, order]);

  // Kemasan Dus Summary (Total Kardus & Rincian Dus Campuran/Lebihan)
  const kemasanSummary = useMemo(() => {
    let rawItems = [];
    if (shipment?.items?.length > 0) {
      rawItems = shipment.items;
    } else if (order?.shipments?.length > 0) {
      order.shipments.forEach(s => {
        if (s.items?.length > 0) rawItems.push(...s.items);
      });
    } else if (order?.shipping_details?.length > 0) {
      rawItems = order.shipping_details;
    }

    if (rawItems.length === 0) return null;

    const p = Math.max(1, Number(shipment?.pcs_per_dus) || Number(order?.pcs_per_dus) || 25);

    const gabungItemsWithRem = rawItems.filter(it => {
      const q = Number(it.qty_akhir) || Number(it.qty_dikirim) || Number(it.qty) || 0;
      const c = Math.max(1, Number(it.pcs_per_dus) || p);
      return (it.sisa_action === 'gabung') && (q % c > 0);
    });

    const isMultiGabung = gabungItemsWithRem.length > 1;

    let totalBoxes = 0;
    let fullBoxesCount = 0;
    let remBoxesCount = 0;
    let mixDetailStr = '';

    if (isMultiGabung) {
      const mixBoxCap = Math.max(1, Number(gabungItemsWithRem[0]?.pcs_per_dus) || p || 65);
      const totalPooledRem = gabungItemsWithRem.reduce((sum, it) => {
        const q = Number(it.qty_akhir) || Number(it.qty_dikirim) || Number(it.qty) || 0;
        const c = Math.max(1, Number(it.pcs_per_dus) || p);
        return sum + (q % c);
      }, 0);

      const isOverflow = totalPooledRem > mixBoxCap;
      const lebihanQty = isOverflow ? (totalPooledRem - mixBoxCap) : 0;
      const targetLebihanVar = shipment?.lebihan_variant || gabungItemsWithRem[gabungItemsWithRem.length - 1].variant_name;

      rawItems.forEach(it => {
        const q = Number(it.qty_akhir) || Number(it.qty_dikirim) || Number(it.qty) || 0;
        const c = Math.max(1, Number(it.pcs_per_dus) || p);
        const rem = q % c;
        if (it.sisa_action === 'gabung' && rem > 0) {
          fullBoxesCount += Math.floor(q / c);
        } else {
          fullBoxesCount += Math.floor(q / c);
          if (rem > 0) remBoxesCount += 1;
        }
      });

      const pooledBoxes = Math.ceil(totalPooledRem / mixBoxCap);
      totalBoxes = fullBoxesCount + remBoxesCount + pooledBoxes;

      if (isOverflow) {
        mixDetailStr = `${fullBoxesCount} Dus Penuh + 1 Dus Campuran (${mixBoxCap} pcs) + 1 Dus Lebihan (${lebihanQty} pcs)`;
      } else {
        mixDetailStr = `${fullBoxesCount} Dus Penuh + 1 Dus Campuran (${totalPooledRem} pcs)`;
      }
    } else {
      rawItems.forEach(it => {
        const q = Number(it.qty_akhir) || Number(it.qty_dikirim) || Number(it.qty) || 0;
        const c = Math.max(1, Number(it.pcs_per_dus) || p);
        const rem = q % c;
        fullBoxesCount += Math.floor(q / c);
        if (rem > 0) remBoxesCount += 1;
      });
      totalBoxes = fullBoxesCount + remBoxesCount;
      mixDetailStr = remBoxesCount > 0 ? `${fullBoxesCount} Dus Penuh + ${remBoxesCount} Dus Sisa` : `${fullBoxesCount} Dus Penuh`;
    }

    return {
      totalBoxes,
      mixDetailStr
    };
  }, [shipment, order]);

  const { data: teamMembers = [] } = useQuery({
    queryKey: ['team-members'],
    queryFn: () => base44.entities.TeamMember.list(),
    staleTime: 60000
  });

  const targetPicRaw = useMemo(() => {
    return (
      picName ||
      shipment?.pic ||
      order?.pic ||
      order?.pic_pengiriman ||
      order?.pic_marketing ||
      companySettings?.nama_pengirim_sj ||
      'Nasrul Idris'
    ).replace(/^@/, '').trim();
  }, [picName, shipment, order, companySettings]);

  const matchedMember = useMemo(() => {
    if (!targetPicRaw) return null;
    const cleanTarget = targetPicRaw.toLowerCase();
    return teamMembers.find((m) => {
      const fn = (m.full_name || m.name || '').trim().toLowerCase();
      const un = (m.username || '').trim().toLowerCase();
      const em = (m.email || '').trim().toLowerCase();
      if (!fn && !un) return false;
      return (
        fn === cleanTarget ||
        un === cleanTarget ||
        cleanTarget.startsWith(fn) ||
        fn.startsWith(cleanTarget) ||
        (cleanTarget.length >= 3 && fn.includes(cleanTarget)) ||
        (un && un === cleanTarget) ||
        (em && em.includes(cleanTarget))
      );
    }) || null;
  }, [teamMembers, targetPicRaw]);

  const hasPic = Boolean(targetPicRaw && targetPicRaw.trim() !== '');
  const finalPic = hasPic 
    ? (matchedMember?.full_name || matchedMember?.name || targetPicRaw)
    : 'PT. SATALA DERMATECH ESSENTIAL';
  const finalJabatan = hasPic 
    ? (picJabatan || matchedMember?.position || matchedMember?.role || 'Penanggung Jawab SJ')
    : '';

  const finalSignatureUrl = useMemo(() => {
    if (!hasPic) return null;
    if (signatureUrl) return signatureUrl;
    if (matchedMember?.signature_url) return matchedMember.signature_url;
    if (matchedMember?.ttd_url) return matchedMember.ttd_url;

    const clean = finalPic.toLowerCase();
    if (clean.includes('annisa')) return '/TTD ANNISA NURUL FADHILAH.png';
    if (clean.includes('yosita')) return '/signature-yosita.png';
    if (clean.includes('nasrul') || clean.includes('noval')) return '/ttd-surat-jalan.png';

    if (companySettings?.nama_pengirim_sj === finalPic && companySettings?.ttd_pengirim_sj_url) {
      return companySettings.ttd_pengirim_sj_url;
    }
    return null;
  }, [hasPic, signatureUrl, matchedMember, finalPic, companySettings]);

  return (
    <div
      ref={documentRef}
      className="bg-white font-sans text-black text-sm leading-relaxed"
      style={{
        width: '210mm',
        minHeight: '297mm',
        padding: '12mm',
        margin: '0 auto',
        boxSizing: 'border-box',
        backgroundColor: '#ffffff'
      }}
    >
      <style type="text/css">{`
        @import url('https://fonts.googleapis.com/css2?family=Dancing+Script:wght@700&display=swap');
        * {
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
      `}</style>

      {/* Header Surat Jalan */}
      <div className="flex justify-between items-start mb-4">
        <div className="flex gap-4 items-center">
          {logoUrl ? (
            <img src={logoUrl} alt="Logo" className="w-14 h-14 object-contain" />
          ) : (
            <div className="w-14 h-14 bg-[#8C6208] rounded-full flex items-center justify-center shrink-0">
              <span className="text-white text-3xl font-bold tracking-tighter">S</span>
            </div>
          )}
          <div>
            <h2 className="text-base font-black uppercase text-[#302621] tracking-tight">{kompanyName}</h2>
            <p className="text-[9px] text-slate-800 font-bold max-w-[420px]">
              {kompanyAddr}
            </p>
          </div>
        </div>
        <div className="text-right">
          <h1 className="text-xl font-black text-slate-800 tracking-wider">SURAT JALAN</h1>
          <span className="inline-block mt-1 text-slate-800 font-bold px-2 py-0.5 rounded text-[10px]">No: {noSurat}</span>
        </div>
      </div>

      <div className="border-b-[3px] border-[#D4AF37] mb-4"></div>

      {/* Detail Pengiriman & Penerima */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="space-y-1 text-xs">
          <div className="flex">
            <span className="w-32 text-slate-800 font-bold">Nomor Invoice PO</span>
            <span className="w-2">:</span>
            <span className="font-bold text-slate-800">#{order?.order_number || order?.id?.split('-')[0] || '-'}</span>
          </div>
          <div className="flex">
            <span className="w-32 text-slate-800 font-bold">Tanggal</span>
            <span className="w-2">:</span>
            <span className="font-bold text-slate-800 flex-1">{isGabungan ? `${order?.shipments?.length} Pengiriman (Gabungan)` : orderDate}</span>
          </div>
          <div className="flex">
            <span className="w-32 text-slate-800 font-bold">Ekspedisi (Via)</span>
            <span className="w-2">:</span>
            <span className="font-bold text-slate-800 flex-1">{isGabungan ? 'Multi-Ekspedisi' : via}</span>
          </div>
          {order?.ongkir_amount > 0 && (
            <div className="flex">
              <span className="w-32 text-slate-800 font-bold">Ongkir</span>
              <span className="w-2">:</span>
              <span className="font-bold text-slate-800 flex-1">
                {isGabungan ? '-' : formatRupiah(shipment?.shipping_cost || order?.ongkir_amount)}
              </span>
            </div>
          )}
          {(shipment?.resi || order?.tracking_number) && (
            <div className="flex">
              <span className="w-32 text-slate-800 font-bold">No. Resi</span>
              <span className="w-2">:</span>
              <span className="font-medium text-slate-800">{shipment?.resi || order?.tracking_number}</span>
            </div>
          )}
        </div>

        <div className="border border-slate-200 bg-slate-50/50 p-2.5 rounded-lg text-xs">
          <p className="font-bold text-slate-800 mb-0.5">Kepada:</p>
          {shipment?.alamat_pengiriman || shipment?.alamat_lengkap ? (
            <>
              {(() => {
                const brandOnly = (order?.brand_name || '').split(' / ')[0].trim();
                const rawPenerima = (shipment.alamat_pengiriman?.penerima || shipment.penerima || '').trim();
                const isPenerimaEmptyOrBrand = !rawPenerima || rawPenerima === '-' || ['client', 'pic', 'asa', 'admin', brandOnly.toLowerCase()].includes(rawPenerima.toLowerCase());
                return (
                  <p className="font-bold text-[#8C6208] text-xs">
                    {brandOnly}{!isPenerimaEmptyOrBrand ? ` / ${rawPenerima}` : ''}
                  </p>
                );
              })()}
              <p className="text-[9px] text-slate-700 mt-0.5 font-bold">
                {shipment.alamat_pengiriman?.telepon || shipment.telepon || '-'}
              </p>
              <p className="text-[9px] text-slate-700 mt-1 leading-tight font-semibold">
                {shipment.alamat_pengiriman?.alamat_lengkap || shipment.alamat_lengkap}
                {(shipment.alamat_pengiriman?.kode_pos || shipment.kode_pos) ? ` (${shipment.alamat_pengiriman?.kode_pos || shipment.kode_pos})` : ''}
              </p>
            </>
          ) : (
            <>
              <p className="font-bold text-[#8C6208] text-xs">
                {(order?.brand_name || '').split(' / ')[0].trim()}
              </p>
              {order?.shipping_address ? (
                <p className="text-[9px] text-slate-700 mt-1 leading-tight font-semibold">
                  {order.shipping_address}
                </p>
              ) : (
                <>
                  <p className="text-[9px] text-slate-700 mt-1 leading-tight font-semibold">
                    Pengiriman produk OEM / Maklon Satala Dermatech
                  </p>
                  <p className="text-[9px] text-slate-700 font-semibold">
                    Di Tempat.
                  </p>
                </>
              )}
            </>
          )}
        </div>
      </div>

      <div className="mb-4 text-xs">
        <p className="font-bold text-slate-700">Dengan hormat,</p>
        <p className="text-slate-800 font-bold text-[10px] mt-0.5">
          Bersama surat jalan ini, kami mengirimkan rincian barang sebagai berikut :
        </p>
      </div>

      {/* Tabel Daftar Barang */}
      <table className="w-full text-left border-collapse mb-4 text-[10px]">
        <thead>
          <tr className="bg-[#302621] text-[#D4AF37]">
            <th className="py-2 px-3 font-bold w-8 text-center rounded-tl-md">No</th>
            <th className="py-2 px-3 font-bold">Nama Produk</th>
            <th className="py-2 px-3 font-bold">Keterangan</th>
            <th className="py-2 px-3 font-bold text-center w-12">Qty</th>
            <th className="py-2 px-3 font-bold text-center w-20 rounded-tr-md">Satuan</th>
          </tr>
        </thead>
        <tbody>
          {items.map((it, idx) => (
            <tr key={idx} className="border-b border-slate-100 even:bg-[#F9F5EC] hover:bg-slate-50/50">
              <td className="py-1.5 px-2 text-center text-slate-500">{idx + 1}</td>
              <td className="py-1.5 px-2 font-semibold text-slate-800">
                {(it.nama || '').replace(/\s*\(\s*\d+\s*ml\s*\)/ig, '').trim()}
              </td>
              <td className="py-1.5 px-2 text-slate-700 font-semibold text-[9px]">{it.keterangan || '-'}</td>
              <td className="py-1.5 px-2 text-center font-bold text-slate-800">
                {it.qty > 0 ? it.qty.toLocaleString('id-ID') : '-'}
              </td>
              <td className="py-1.5 px-2 text-center text-slate-700 font-semibold">{it.satuan}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Total Kemasan Dus & Total Kuantitas */}
      <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center bg-[#F9F5EC] border-t-2 border-[#D4AF37] px-4 py-2 rounded-bl-md rounded-br-md mb-4 gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-bold text-slate-700 text-[10px] uppercase tracking-wider">Total Kemasan (Koli):</span>
          <span className="font-black text-[#302621] text-xs bg-white px-2.5 py-0.5 rounded border border-[#E6D5B8]">
            {kemasanSummary ? `${kemasanSummary.totalBoxes} Dus` : '-'}
          </span>
          {kemasanSummary?.mixDetailStr && (
            <span className="text-[9px] text-slate-600 font-medium">
              ({kemasanSummary.mixDetailStr})
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 justify-between sm:justify-end border-t sm:border-t-0 border-amber-200/60 pt-1 sm:pt-0">
          <span className="font-bold text-slate-700 text-[10px] uppercase tracking-wider">Total Kuantitas:</span>
          <span className="font-black text-[#8C6208] text-sm">
            {(() => {
              const poItems = items.filter(i => !i.is_custom);
              const customItems = items.filter(i => i.is_custom);
              const totalPoQty = poItems.reduce((sum, i) => sum + (Number(i.qty) || 0), 0);
              
              if (customItems.length === 0) {
                return `${totalPoQty.toLocaleString('id-ID')} pcs`;
              }

              const parts = [];
              if (totalPoQty > 0 || poItems.length > 0) {
                parts.push(`${totalPoQty.toLocaleString('id-ID')} pcs`);
              }
              
              const customByUnit = {};
              customItems.forEach(ci => {
                const u = ci.satuan || 'item';
                customByUnit[u] = (customByUnit[u] || 0) + (Number(ci.qty) || 0);
              });
              Object.entries(customByUnit).forEach(([u, q]) => {
                parts.push(`${q.toLocaleString('id-ID')} ${u}`);
              });

              return parts.join(' + ');
            })()}
          </span>
        </div>
      </div>

      {/* Catatan / Keterangan Tambahan (Selalu ditampilkan, default 'Catatan: -' jika kosong) */}
      <div className="mb-6 bg-[#fffbeb] p-2.5 rounded-lg border border-[#fde68a] text-[10px] text-[#92400e]">
        <strong>Catatan:</strong> {(shipment?.catatan_surat_jalan || order?.notes) || '-'}
      </div>

      {/* 2 Kolom Tanda Tangan: Diterima Oleh & PT Satala (Driver ditiadakan) */}
      <div className="flex justify-between items-start mt-8 pt-4 border-t border-slate-200 text-xs px-6 sm:px-12">
        {/* Penerima (Dikosongkan untuk diisi tanda tangan & nama jelas secara manual) */}
        <div className="text-center flex flex-col justify-between h-36 w-56">
          <p className="text-[10px] text-slate-800 font-bold">Diterima Oleh,</p>
          <div className="w-full border-t border-slate-300 pt-1 mt-auto">
            <p className="font-medium text-slate-400 text-[10px] tracking-wider">
              (&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;)
            </p>
            <p className="text-slate-500 text-[8px] uppercase tracking-wider mt-0.5">Nama Jelas & Cap</p>
          </div>
        </div>

        {/* PT Satala (Mengikuti PIC) */}
        <div className="text-center flex flex-col justify-between h-36 w-56 relative">
          <div>
            <p className="text-[9px] text-slate-500">
              {kota ? `${kota},` : 'Bogor,'} {orderDate.split(' ').slice(1).join(' ')}
            </p>
            <p className="text-[10px] text-slate-800 font-bold mt-0.5">Hormat Kami,</p>
            <p className="text-[9px] text-[#8C6208] font-bold tracking-tight">PT. SATALA DERMATECH ESSENTIAL</p>
          </div>
          <div className="relative w-full h-16 my-auto flex justify-center items-center">
            {finalSignatureUrl ? (
              <>
                {stempelUrl && (
                  <img
                    src={stempelUrl}
                    alt="Stempel"
                    className="absolute left-6 -top-2 pointer-events-none w-20 h-20 object-contain opacity-85"
                  />
                )}
                <img
                  src={finalSignatureUrl}
                  alt={`Tanda Tangan ${finalPic}`}
                  className="w-28 h-16 object-contain relative z-10"
                />
              </>
            ) : (
              /* Jika PIC belum memiliki file tanda tangan, jangan dipaksa: tampilkan stempel saja di tengah */
              stempelUrl && (
                <img
                  src={stempelUrl}
                  alt="Stempel"
                  className="w-20 h-20 object-contain opacity-85 pointer-events-none mx-auto"
                />
              )
            )}
          </div>
          <div className="w-full border-t border-slate-300 pt-1 mt-auto">
            <p className="font-bold text-slate-800 text-[10px]">
              {finalPic}
            </p>
            {finalJabatan && (
              <p className="text-slate-700 font-bold text-[8px] uppercase tracking-wider">
                {finalJabatan}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
